"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useMemo } from "react";
import { debounce } from "lodash";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { useQueryClient } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiDropsLeaderboardPage } from "@/generated/models/ApiDropsLeaderboardPage";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { WAVE_DROPS_PARAMS, getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
interface WaveleaderboardSortProps {
  readonly sort: WaveDropsLeaderboardSort;
  readonly onSortChange: (sort: WaveDropsLeaderboardSort) => void;
  readonly waveId?: string | undefined;
}
const SORT_DIRECTION_MAP: Record<WaveDropsLeaderboardSort, string | undefined> = {
  [WaveDropsLeaderboardSort.RANK]: undefined,
  [WaveDropsLeaderboardSort.RATING_PREDICTION]: "DESC",
  [WaveDropsLeaderboardSort.MY_REALTIME_VOTE]: undefined,
  [WaveDropsLeaderboardSort.CREATED_AT]: "DESC"
};
export const WaveleaderboardSort: React.FC<WaveleaderboardSortProps> = t0 => {
  const $ = _c(36);
  const {
    sort,
    onSortChange,
    waveId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== queryClient || $[1] !== sort || $[2] !== waveId) {
    t1 = targetSort => {
      if (!waveId || targetSort === sort) {
        return;
      }
      const sortDirection = SORT_DIRECTION_MAP[targetSort];
      const queryKey = [QueryKey.DROPS_LEADERBOARD, {
        waveId,
        page_size: WAVE_DROPS_PARAMS.limit,
        sort: targetSort,
        sort_direction: sortDirection
      }];
      queryClient.prefetchInfiniteQuery({
        queryKey,
        queryFn: async t2 => {
          const {
            pageParam
          } = t2;
          const params = {
            page_size: WAVE_DROPS_PARAMS.limit.toString(),
            sort: targetSort
          };
          if (sortDirection) {
            params.sort_direction = sortDirection;
          }
          if (pageParam) {
            params.page = `${pageParam}`;
          }
          return await commonApiFetch({
            endpoint: `waves/${waveId}/leaderboard`,
            params
          });
        },
        initialPageParam: null,
        getNextPageParam: lastPage => {
          if (targetSort === WaveDropsLeaderboardSort.MY_REALTIME_VOTE) {
            const haveZeroVotes = lastPage.drops.some(_temp);
            if (haveZeroVotes) {
              return null;
            }
          }
          return lastPage.next ? lastPage.page + 1 : null;
        },
        pages: 1,
        staleTime: 60000,
        ...getDefaultQueryRetry()
      }).catch(error => {
        console.warn("Failed to prefetch leaderboard data:", {
          waveId,
          targetSort,
          error: error.message || error
        });
      });
    };
    $[0] = queryClient;
    $[1] = sort;
    $[2] = waveId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const prefetchSortImmediate = t1;
  let t2;
  if ($[4] !== prefetchSortImmediate) {
    t2 = debounce(prefetchSortImmediate, 300);
    $[4] = prefetchSortImmediate;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const prefetchSort = t2;
  let t3;
  if ($[6] !== sort) {
    t3 = buttonSort => {
      if (sort === buttonSort) {
        return "tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-border-0 tw-rounded-md tw-transition-colors tw-bg-white/10 tw-text-white tw-shadow-sm";
      }
      return "tw-px-4 tw-py-1.5 tw-text-xs tw-font-medium tw-border-0 tw-rounded-md tw-transition-colors tw-bg-transparent tw-text-iron-500 desktop-hover:hover:tw-text-iron-300";
    };
    $[6] = sort;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const getButtonClassName = t3;
  const t4 = getButtonClassName(WaveDropsLeaderboardSort.RANK);
  let t5;
  if ($[8] !== onSortChange) {
    t5 = () => onSortChange(WaveDropsLeaderboardSort.RANK);
    $[8] = onSortChange;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== prefetchSort) {
    t6 = () => prefetchSort(WaveDropsLeaderboardSort.RANK);
    $[10] = prefetchSort;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <button className={t4} onClick={t5} onMouseEnter={t6}>Current Vote</button>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = getButtonClassName(WaveDropsLeaderboardSort.RATING_PREDICTION);
  let t9;
  if ($[16] !== onSortChange) {
    t9 = () => onSortChange(WaveDropsLeaderboardSort.RATING_PREDICTION);
    $[16] = onSortChange;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== prefetchSort) {
    t10 = () => prefetchSort(WaveDropsLeaderboardSort.RATING_PREDICTION);
    $[18] = prefetchSort;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t8 || $[22] !== t9) {
    t11 = <button className={t8} onClick={t9} onMouseEnter={t10}>Projected Vote</button>;
    $[20] = t10;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = getButtonClassName(WaveDropsLeaderboardSort.CREATED_AT);
  let t13;
  if ($[24] !== onSortChange) {
    t13 = () => onSortChange(WaveDropsLeaderboardSort.CREATED_AT);
    $[24] = onSortChange;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== prefetchSort) {
    t14 = () => prefetchSort(WaveDropsLeaderboardSort.CREATED_AT);
    $[26] = prefetchSort;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = <button className={t12} onClick={t13} onMouseEnter={t14}>Newest</button>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t11 || $[33] !== t15 || $[34] !== t7) {
    t16 = <div id="tabsId" className="tw-flex tw-bg-iron-950 tw-p-1 tw-rounded-lg tw-border tw-border-solid tw-border-white/10">{t7}{t11}{t15}</div>;
    $[32] = t11;
    $[33] = t15;
    $[34] = t7;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
};
function _temp(drop) {
  return drop.context_profile_context?.rating === 0;
}